import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { AgentMlsIdResource } from "api/resource/agent-mls-id";
import { RefreshTable } from "component/shared/refresh-table";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { Tag } from "component/shared/tag";
import { AgentMlsIds } from "model/agent-mls-ids";
import * as paths from "routes/paths";
import { profileSplit } from "../profile-split";
import { styles } from "./style";

// The agent's MLS IDs on their profile, as MlsIds (../mls-ids) shows them.

interface Actions {
	onEdit?: (entry: AgentMlsIdResource) => void;
	onSet?: (boardId: number) => void;
	onRemove?: (entry: AgentMlsIdResource) => void;
}

interface ViewProps extends Actions, WithStyles<typeof styles> {
	agentMlsIds: AgentMlsIds;
	agentId: number;
	// Makes a non-billable agent billable, from the banner above their greyed MLS IDs. Without it there's no button.
	onMakeBillable?: () => void;
	// An admin can resolve an ID's issue, so their tooltip links to the page that does it.
	admin?: boolean;
}

const AMBER = "#B26A00";
const TEAL = "#36747B";
const AGENT_ONLY_TIP = "Kept off the Featured Listings page. This MLS ID still powers the bio page and lead routing.";
const INTRO = "Your Agent MLS ID connects your listings to your bio page, lead routing and Featured Listings. On Featured Listings, Office MLS IDs are used only if no agent on the account has active listings.";
const LOCKED_LINK = "Featured Listings page";
const LOCKED_TIP = `This ID can't be edited until an admin resolves its issue on the ${LOCKED_LINK}.`;
const LOCKED_TIP_ADMIN = `This ID can't be edited until its issue is resolved on the ${LOCKED_LINK}.`;
const OFFICE_ID_TIP = "Used only if none of your Agent MLS IDs return any listings.";
const STRAY_TIP = "This MLS ID isn't on Featured Listings. It still connects listings to the bio page and lead routing, but can't be edited. Remove it.";
// On an office-sourced account, Featured Listings run on the Office MLS IDs, so an agent's MLS IDs never reach it:
// nothing here mentions Featured Listings or the Office MLS ID.
const INTRO_OFFICE = "Your Agent MLS ID connects your listings to your bio page and lead routing.";
const MULTIPLE_TIP_OFFICE = "This board already has an MLS ID on this profile. Extra IDs still connect listings to the bio page and lead routing, but can't be edited. We recommend removing them.";

interface MainRow {
	key: string;
	boardId: number;
	boardName: string;
	pending: boolean;
	entry: AgentMlsIdResource | null;
}

interface StrayRow {
	boardName: string;
	pending: boolean;
	entry: AgentMlsIdResource;
}

const Component: React.FC<ViewProps> = ({ agentMlsIds, agentId, onEdit, onSet, onRemove, onMakeBillable, admin = false, classes = {} }) => {
	const boardName = new Map(agentMlsIds.boards.map((board) => [board.boardId, board.name]));
	const split = profileSplit(agentMlsIds, agentId);
	const mainRows: MainRow[] = split.flatMap(({ boardId, pending, ids }): MainRow[] => {
		const row = { boardId, boardName: boardName.get(boardId) || "", pending };
		return ids.length ? ids.map((entry) => ({ ...row, key: `id-${entry.id}`, entry })) : [{ ...row, key: `board-${boardId}`, entry: null }];
	});
	const strayRows: StrayRow[] = split.flatMap(({ boardId, pending, strays }) =>
		strays.map((entry) => ({ boardName: boardName.get(boardId) || "", pending, entry }))
	);
	const office = agentMlsIds.officeAccount;
	// A non-billable agent can't manage MLS IDs or show listings on Featured Listings until they're made billable. On an
	// office-sourced account an agent's MLS IDs never reach Featured Listings, so nothing is greyed there.
	const agent = agentMlsIds.agents.get(agentId) ?? null;
	const notBillable = !office && agent?.billable === false;
	const firstName = (agent?.name || "").split(" ")[0];
	const edit = notBillable ? undefined : onEdit;
	const set = notBillable ? undefined : onSet;
	const remove = notBillable ? undefined : onRemove;
	// On an agent-sourced account an ID needs review because it has no Featured row; on an office-sourced one, because
	// the board has more than one.
	const strayTag = office ? { label: "Multiple IDs", tip: MULTIPLE_TIP_OFFICE } : { label: "Not on Featured", tip: STRAY_TIP };

	const [lockedBefore, lockedAfter] = LOCKED_TIP_ADMIN.split(LOCKED_LINK);
	const lockedTip = admin ? (
		<>
			{lockedBefore}
			<Mui.Link component={RouterLinkWrapper} to={paths.settingsListingResults} color="inherit" underline="always">{LOCKED_LINK}</Mui.Link>
			{lockedAfter}
		</>
	) : LOCKED_TIP;

	// A board pending approval is tagged: its listings won't display until it's approved.
	const boardCell = (row: { boardName: string; pending: boolean }) => (
		<span className={classes.boardCell}>
			{row.boardName}
			{row.pending && <Tag label="Pending Approval" variant="neutral" size="small" />}
		</span>
	);

	// The Office MLS ID of the ID's Featured row. An ID that isn't on Featured Listings, kept off it included, has none.
	const officeIdCell = (entry: AgentMlsIdResource) => {
		const link = agentMlsIds.clientBoardLinks.get(entry.id);
		if (link?.state !== "linked") {
			return <span className={classes.muted}>—</span>;
		}
		return link.alternateId || <span className={classes.muted}>None</span>;
	};

	// A check where the ID shows on Featured Listings, or an Info icon where it's kept off it.
	const onFeaturedCell = (entry: AgentMlsIdResource) => {
		const state = agentMlsIds.clientBoardLinks.get(entry.id)?.state;
		if (state === "off") {
			return (
				<Mui.Tooltip title={AGENT_ONLY_TIP} arrow>
					<span className={classes.tipIcon} tabIndex={0} aria-label={AGENT_ONLY_TIP}>
						<Icons.Info size={16} color={TEAL} />
					</span>
				</Mui.Tooltip>
			);
		}
		return state === "linked" && !notBillable ? <span role="img" aria-label="Yes"><Icons.Check size={20} /></span> : null;
	};

	return (
		<div>
			<Mui.Typography className={classes.intro}>{office ? INTRO_OFFICE : INTRO}</Mui.Typography>
			{notBillable && (
				<div className={classes.notBillable} role="alert">
					<Icons.AlertTriangle size={16} color={AMBER} />
					<span className={classes.notBillableText}>{`${firstName} needs to be billable to manage MLS IDs and show listings on Featured Listings.`}</span>
					{onMakeBillable && <Mui.Button size="small" onClick={onMakeBillable}>{`Make ${firstName} Billable`}</Mui.Button>}
				</div>
			)}
			<div className={notBillable ? classes.greyed : undefined} aria-disabled={notBillable || undefined}>
				{strayRows.length > 0 && (
					<Mui.Accordion className={classes.review} disableGutters>
						<Mui.AccordionSummary expandIcon={<Icons.ChevronDown size={18} />}>
							<span className={classes.reviewTitle}>
								<Icons.AlertTriangle size={16} color={AMBER} />
								{strayRows.length === 1 ? "1 MLS ID needs review" : `${strayRows.length} MLS IDs need review`}
							</span>
						</Mui.AccordionSummary>
						<Mui.AccordionDetails>
							<RefreshTable
								items={strayRows}
								rowKey={(row) => row.entry.id}
								sort={{ id: "", desc: false }}
								paginate={false}
								columns={[
									{ id: "board", title: "MLS Board", value: (row) => boardCell(row) },
									{
										id: "mlsId",
										title: "Agent MLS ID",
										value: (row) => (
											<span className={classes.codeLine}>
												{row.entry.mlsId}
												<Mui.Tooltip title={strayTag.tip} arrow>
													<span tabIndex={0} aria-label={strayTag.tip}>
														<Tag label={strayTag.label} variant="warning" size="small" icon={<Icons.AlertTriangle size={12} />} />
													</span>
												</Mui.Tooltip>
											</span>
										),
									},
									{
										id: "actions",
										title: "Action",
										align: "right",
										value: (row) => (
											<Mui.IconButton size="small" aria-label="Remove" title="Remove" disabled={!remove} onClick={() => remove && remove(row.entry)}>
												<Icons.Trash2 size={18} />
											</Mui.IconButton>
										),
									},
								]}
							/>
						</Mui.AccordionDetails>
					</Mui.Accordion>
				)}
				<RefreshTable
					items={mainRows}
					rowKey={(row) => row.key}
					sort={{ id: "", desc: false }}
					paginate={false}
					columns={[
						{ id: "board", title: "MLS Board", value: (row) => boardCell(row) },
						{
							id: "mlsId",
							title: "Agent MLS ID",
							value: (row) => row.entry ? row.entry.mlsId : <span className={classes.muted}>None</span>,
						},
						{
							id: "officeId",
							hidden: office,
							title: (
								<span className={classes.codeLine}>
									Office MLS ID
									<Mui.Tooltip title={OFFICE_ID_TIP} arrow>
										<span className={classes.tipIcon} tabIndex={0} aria-label="About Office MLS ID">
											<Icons.Info size={14} />
										</span>
									</Mui.Tooltip>
								</span>
							),
							value: (row) => row.entry ? officeIdCell(row.entry) : <span className={classes.muted}>None</span>,
						},
						{
							id: "onFeatured",
							hidden: office,
							title: "On Featured Listings",
							value: (row) => row.entry ? onFeaturedCell(row.entry) : null,
						},
						{
							id: "actions",
							title: "Actions",
							align: "right",
							value: (row) => {
								const entry = row.entry;
								// An ID that isn't aligned is fixed on the Featured Listings page first, and the tooltip says who can.
								const handler = entry ? (edit && entry.aligned ? () => edit(entry) : undefined) : set && (() => set(row.boardId));
								const button = (
									<Mui.IconButton size="small" aria-label="Edit" title={handler || !entry ? "Edit" : undefined} disabled={!handler} onClick={handler}>
										<Icons.Edit2 size={18} />
									</Mui.IconButton>
								);
								return entry && edit && !entry.aligned ? (
									<Mui.Tooltip title={lockedTip} arrow>
										<span tabIndex={0} aria-label={admin ? LOCKED_TIP_ADMIN : LOCKED_TIP}>{button}</span>
									</Mui.Tooltip>
								) : button;
							},
						},
					]}
				/>
			</div>
		</div>
	);
};

export const MlsIdsView = withStyles(Component, styles);
